import * as React from 'react'
import * as SwitchPrimitives from '@radix-ui/react-switch'

import { cn } from '@/utils/common'

type SwitchRootProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitives.Root
>

interface SwitchProps extends SwitchRootProps {
  size?: 'sm' | 'md' | 'lg'
}

const rootSizeClasses: Record<NonNullable<SwitchProps['size']>, string> = {
  sm: 'h-5 w-9',
  md: 'h-6 w-11',
  lg: 'h-8 w-14',
}

const thumbSizeClasses: Record<NonNullable<SwitchProps['size']>, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-7 w-7',
}

const thumbTranslateClasses: Record<
  NonNullable<SwitchProps['size']>,
  string
> = {
  sm: 'data-[state=checked]:translate-x-4',
  md: 'data-[state=checked]:translate-x-5',
  lg: 'data-[state=checked]:translate-x-7',
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, size = 'md', ...props }, ref) => (
  <SwitchPrimitives.Root
    ref={ref}
    className={cn(
      'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
      'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-300',
      'p-0',
      rootSizeClasses[size],
      className
    )}
    {...props}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform',
        'data-[state=unchecked]:translate-x-0',
        thumbSizeClasses[size],
        thumbTranslateClasses[size]
      )}
    />
  </SwitchPrimitives.Root>
))
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
